/* UsersDashboard.jsx */
import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";

import PeriodSelect from "./PeriodSelect";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DataTable from "examples/Tables/DataTable";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import crudsService from "services/cruds-service";

/* helpers ────────────────────────────────────────── */
const msToS = ms => Math.round(ms / 1000);
const pickColors = n => {
  const palette = [
    "info", "dark", "error", "secondary", "primary", "warning", "success",
  ];
  return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
};

export default function UsersDashboard() {
  const [period, setPeriod] = useState("yesterday");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  /* fetch once per period change */
  useEffect(() => {
    setBusy(true);
    crudsService.getAggDaily(period)
      .then(r => {
        setDoc(r.data[0].user_metrics)
      })
      .finally(() => setBusy(false));
  }, [period]);

  /* time-series: DAU per day (array may be empty during “yesterday”) */
  const dauSeries = useMemo(() => {
    const pts = doc?.timeseries?.dau_per_day ?? [];
    return {
      labels: pts.map(p => p.x),
      datasets: { label: "DAU", color: "info", data: pts.map(p => p.y) },
    };
  }, [doc]);

  /* bar chart: logins per hour */
  const loginsBar = useMemo(() => {
    const hrs = doc?.timeseries?.logins_per_hour ?? Array(24).fill(0);
    return {
      labels: hrs.map((_, h) => {
        const t = new Date(0, 0, 0, h, 0, 0);
        return `${t.toLocaleString("en-US", { hour: "numeric", hour12: true })}`;
      }),
      datasets: [{ label: "Logins", data: hrs, color: "info" }],
    };
  }, [doc]);

  /* funnel numbers for VerticalBarChart */
  const funnelChart = useMemo(() => {
    const f = doc?.extras?.funnel ?? {};
    return {
      labels: ["Registrations", "Enrolments", "Assessments", "Certificates"],
      datasets: [{
        label: "Users", color: "primary", data: [
          f.reg || 0, f.enrol || 0, f.asses || 0, f.cert || 0,
        ]
      }],
    };
  }, [doc]);

  if (busy || !doc)
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );

  const { kpi, pie, timeseries, extras, generated_at } = doc;

  return (
    <>
      <MDBox mb={2}>
        {/* <PeriodSelect value={period} onChange={setPeriod} /> */}
      </MDBox>

           {/* ─── KPI section ────────────────────────── */}
      <MDTypography variant="h4">Headline KPIs</MDTypography>
      <MDTypography variant="button">
        Data updated on&nbsp; 
        {/* add midnight today's time */}
        <strong>{new Date(generated_at).toLocaleDateString()}</strong>
      </MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Total users" }} count={kpi.total_users} icon={{ component: "people", color: "primary" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "New registrations" }} count={kpi.new_registrations} icon={{ component: "person_add", color: "success" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "DAU" }} count={kpi.dau} icon={{ component: "leaderboard", color: "info" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Avg session time (in s)" }} count={msToS(kpi.avg_session_ms)} icon={{ component: "timer", color: "warning" }} />
        </Grid>
      </Grid>

      {/* ─── Time-series section ────────────────── */}
      <Divider />
      <MDTypography variant="h4" mb={1}>Activity over time</MDTypography>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={12}>
          <ReportsLineChart
            color="info"
            title="Daily Active Sessions"
            description="Rolling DAS"
            date={`updated ${period.replace("_", " ")}`}
            chart={dauSeries}
          />
        </Grid>
      </Grid>

      {/* ─── Funnel bar ─────────────────────────── */}
      <Grid container spacing={3} mt={1}>

        <Grid item xs={12} md={6}>
          <VerticalBarChart
            icon={{ component: "timeline", color: "dark" }}
            title="App visits per hour"
            chart={loginsBar}
            description="UTC hours"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <VerticalBarChart
            icon={{ component: "filter_alt", color: "warning" }}
            title="Funnel (rolling 30 d)"
            chart={funnelChart}
            description="Registrations → Certificates"
          />
        </Grid>
      </Grid>

     

      {/* ─── Top active users table ─────────────── */}
      <Divider />
      <MDTypography variant="h4" mb={2}>Top active users</MDTypography>

      <DataTable
        isSorted={false}
        showTotalEntries={false}
        entriesPerPage={false}
        table={{
          columns: [
            { Header: "User", accessor: "user", id: "user", width: "70%" },
            { Header: "Sessions", accessor: "count", id: "count", width: "30%" },
          ],
          rows: extras.top_active_users.map(u => ({
            user: u.userId || u.name || "-",
            count: u.sessions,
          })),
        }}
      />
    </>
  );
}
